// Dependency-free helpers for contour layer names and intervals. Kept free of
// relative imports so node --test can load it directly.
export const isContourName = (name) => {
    const lowerName = name.toLowerCase();
    return lowerName.includes('contour') ||
        lowerName === '0.5m' || lowerName === '1m' || lowerName === '2m' || lowerName === '3m' ||
        /^\d+(\.\d+)?m$/.test(lowerName) || /^\d+(\.\d+)?ft$/.test(lowerName);
};
// --- Low-resolution scouting contour gating --------------------------------
// On the low-res scouting DEM (~15m of real information) only the coarsest
// contour intervals are meaningful — finer lines trace sampling noise, not
// real ground. These per-unit floors are the smallest intervals that survive;
// anything below is gated behind a full-resolution unlock (ContourResolutionModal).
// User-facing rule: 8ft+ (imperial) and 2m+ (metric) work in low-res. These
// mirror the backend's scoutingMinContourIntervalMeters (2.0m): everything the
// gate ALLOWS is actually generated for the scouting set, so nothing 404s.
export const LOW_RES_MIN_CONTOUR_FT = 8;
export const LOW_RES_MIN_CONTOUR_M = 2;
// parseContourInterval pulls the numeric interval + unit out of a contour
// artifact name (e.g. "contours_4ft_display.kml" -> {value:4, unit:'ft'},
// "2m" -> {value:2, unit:'m'}). Returns null for non-contour / unparseable
// names so callers fail open.
export const parseContourInterval = (name) => {
    // Matches every shape the interval shows up in: "contours_2ft",
    // "contours_2ft_display", "contours_0.5m.geojson", "contours_12ft.kml".
    // The unit is followed by "_" (e.g. _display), "." (extension), or the end of
    // the string — NOT a \b assertion, since "_" is a word char and would defeat
    // the boundary right after the unit in "..._2ft_display".
    const prefixed = name.match(/contours?_([0-9]*\.?[0-9]+)(ft|m)(?:[_.]|$)/i);
    // Bare interval labels like "2ft" / "0.5 m".
    const bare = prefixed ? null : name.match(/^([0-9]*\.?[0-9]+)\s*(ft|m)$/i);
    const match = prefixed !== null && prefixed !== void 0 ? prefixed : bare;
    if (!match)
        return null;
    const value = parseFloat(match[1]);
    if (!isFinite(value) || value <= 0)
        return null;
    return { value, unit: match[2].toLowerCase() === 'ft' ? 'ft' : 'm' };
};
// isContourBlockedAtLowRes is true when a contour interval is too fine to be
// meaningful on the low-res scouting DEM and should be gated behind an unlock.
export const isContourBlockedAtLowRes = (name) => {
    const parsed = parseContourInterval(name);
    if (!parsed)
        return false;
    return parsed.unit === 'ft'
        ? parsed.value < LOW_RES_MIN_CONTOUR_FT
        : parsed.value < LOW_RES_MIN_CONTOUR_M;
};
// formatContourInterval renders a parsed interval for display, e.g. "4 ft".
export const formatContourInterval = (name) => {
    const parsed = parseContourInterval(name);
    return parsed ? `${parsed.value} ${parsed.unit}` : name;
};
